import React from "react";
import ProductCard from "../productCard/ProductCard";
import { PrimaryPrice } from "../priceComponents";
import { COMPLEMENTARY_PRIMARY_PRICE } from "../priceComponents/priceTypes";
import { ProductContextProvider } from "../../context/product/ProductContext";
import {
  VariantContextProvider,
  useVariant,
} from "../../context/variant/VariantContext";
import { getPrimaryImage, getPrimaryVariant } from "../../utils/product";
import { StylingLookProductWithStockPrice } from "../../types/productTypes/stylingLookProduct";
import {
  fireWaysToStyleAddToBagAnalytics,
  fireWaysToStyleAddToBagMarketing,
} from "../../analytics/addToBag";
import saveForLater from "../../utils/saveForLater";
import { fireSaveForLaterMarketing } from "../../utils/marketingTracking/marketingTrackingEvents";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import getBtlCarouselProductLink from "../../utils/getBtlCarouselProductLink";
import fireNotifyMeClickAnalytics from "../../analytics/backInStock";
import { PAGE_AREAS } from "../../constants";

interface WaysToStyleProductCardProps {
  product: StylingLookProductWithStockPrice;
  looksProducts: StylingLookProductWithStockPrice[];
}

const WaysToStyleProductCardContent: React.FC<WaysToStyleProductCardProps> = ({
  product,
  looksProducts,
}) => {
  const { productPrice } = product;
  const { selectedVariant } = useVariant() as {
    selectedVariant?: { variantId: number };
  };
  const { isMobile } = useRuntimeConstants() as { isMobile: boolean };
  const defaultColourWayId = getPrimaryVariant(product.variants)?.colourWayId;
  const variantIdentifier = selectedVariant
    ? `variantId:${selectedVariant.variantId}`
    : `colourWayId:${defaultColourWayId}`;

  const handleSaveClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const identifier = selectedVariant
      ? { variantId: selectedVariant.variantId }
      : { colourWayId: defaultColourWayId };

    const variantsAdded = selectedVariant
      ? [selectedVariant]
      : [getPrimaryVariant(product.variants)];

    saveForLater({
      product,
      items: [{ productId: product.id, ...identifier }],
      pageArea: PAGE_AREAS.WAYS_TO_STYLE,
      looksProducts: looksProducts,
    });

    fireSaveForLaterMarketing({
      product,
      variantsAdded,
      component_name: "pdp_wts",
    });
  };

  const handleAddToBag: NonNullable<
    React.ComponentProps<typeof ProductCard>["fireAddToBagAnalytics"]
  > = async ({ selectedVariant }) => {
    if (!selectedVariant) {
      return;
    }
    fireWaysToStyleAddToBagAnalytics(looksProducts)({ selectedVariant });
    fireWaysToStyleAddToBagMarketing()({
      selectedVariant,
      product: product,
    });
  };

  const enhanceNotifyMeClickAnalytics = (selectedVariantId: number) => {
    fireNotifyMeClickAnalytics(
      selectedVariantId,
      PAGE_AREAS.WAYS_TO_STYLE,
      looksProducts
    );
  };

  return (
    <ProductCard
      product={product}
      productLink={getBtlCarouselProductLink({
        products: looksProducts,
        product,
        isMobile,
      })}
      handleSaveClick={handleSaveClick}
      variantIdentifier={variantIdentifier}
      variantSelectorId={`ways-to-style-variant-selector-${product.id}`}
      imageWidth={163}
      primaryImageUrl={getPrimaryImage(product.images)?.url ?? ""}
      fireAddToBagAnalytics={handleAddToBag}
      fireNotifyMeClickAnalytics={enhanceNotifyMeClickAnalytics}
      priceRenderProp={() =>
        productPrice ? (
          <PrimaryPrice
            priceData={productPrice}
            testId="product-price"
            priceType={COMPLEMENTARY_PRIMARY_PRICE}
          />
        ) : null
      }
    />
  );
};

const WaysToStyleProductCard: React.FC<WaysToStyleProductCardProps> = ({
  product,
  looksProducts,
}) => {
  return (
    <ProductContextProvider products={[product]} productId={product.id}>
      <VariantContextProvider context="pdp_wts">
        <WaysToStyleProductCardContent
          product={product}
          looksProducts={looksProducts}
        />
      </VariantContextProvider>
    </ProductContextProvider>
  );
};

export default WaysToStyleProductCard;
